import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const statsTrendsSchema = z.object({
  variant: z.enum(['lime', 'ma']).default('lime'),
  eyebrow: z.string().max(120).optional(),
  title: z.string().min(1).max(200),
  titleUnderline: z.string().max(120).optional(),
  titleTail: z.string().max(200).optional(),
  columns: z
    .array(
      z.object({
        chart: z.enum(['line', 'bars', 'wave']).default('line'),
        value: z.string().min(1).max(40),
        label: z.string().min(1).max(200),
        delta: z.string().max(120).optional(),
      }),
    )
    .min(2)
    .max(4),
  ctas: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        href: z.string().max(500),
        style: z.enum(['primary', 'outline']).default('primary'),
      }),
    )
    .max(3)
    .optional(),
});

export type StatsTrendsData = z.infer<typeof statsTrendsSchema>;

function ChartLine() {
  return (
    <svg viewBox="0 0 80 24" preserveAspectRatio="none">
      <path d="M0 20 L13 16 L26 18 L40 10 L53 12 L66 5 L80 2" stroke="currentColor" strokeWidth="2" fill="none" />
      <circle cx="80" cy="2" r="3" fill="currentColor" />
    </svg>
  );
}

function ChartBars() {
  return (
    <svg viewBox="0 0 80 24" preserveAspectRatio="none">
      <rect x="0" y="14" width="8" height="10" fill="currentColor" opacity="0.4" />
      <rect x="12" y="10" width="8" height="14" fill="currentColor" opacity="0.5" />
      <rect x="24" y="12" width="8" height="12" fill="currentColor" opacity="0.6" />
      <rect x="36" y="6" width="8" height="18" fill="currentColor" opacity="0.7" />
      <rect x="48" y="8" width="8" height="16" fill="currentColor" opacity="0.8" />
      <rect x="60" y="2" width="8" height="22" fill="currentColor" opacity="0.9" />
      <rect x="72" y="0" width="8" height="24" fill="currentColor" />
    </svg>
  );
}

function ChartWave() {
  return (
    <svg viewBox="0 0 80 24" preserveAspectRatio="none">
      <path d="M0 22 L80 22" stroke="currentColor" strokeWidth="1" opacity="0.2" />
      <path d="M0 18 C20 12, 30 10, 40 8 C55 6, 65 4, 80 2" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

const charts = { line: ChartLine, bars: ChartBars, wave: ChartWave };

export function StatsTrendsRenderer({
  variant = 'lime',
  eyebrow,
  title,
  titleUnderline,
  titleTail,
  columns,
  ctas,
}: StatsTrendsData) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeCtas = Array.isArray(ctas) ? ctas : [];
  return (
    <section className={`v5-band-${variant}`}>
      <div className="v5-band-bg-grid"></div>
      <div className="container v5-band-inner">
        {eyebrow ? <div className="v5-band-eyebrow">{eyebrow}</div> : null}
        <h2 className="v5-band-h2">
          {title}
          {titleUnderline ? (
            <>
              <br />
              <span className="v5-band-underline">{titleUnderline}</span>
            </>
          ) : null}
          {titleTail ? (
            <>
              <br />
              {titleTail}
            </>
          ) : null}
        </h2>
        <div className="v5-band-cols">
          {safeColumns.map((col, i) => {
            const Chart = charts[col.chart] ?? ChartLine;
            return (
              <div key={i} className="v5-band-col">
                <div className="v5-band-col-trend">
                  <Chart />
                </div>
                <div className="v5-band-num">{col.value}</div>
                <div className="v5-band-lbl">{col.label}</div>
                {col.delta ? <div className="v5-band-delta">{col.delta}</div> : null}
              </div>
            );
          })}
        </div>
        {safeCtas.length > 0 ? (
          <div className="v5-band-ctas">
            {safeCtas.map((cta, i) => (
              <a
                key={i}
                href={cta.href}
                className={cta.style === 'outline' ? 'v5-btn-outline-dark' : 'v5-btn-dark'}
              >
                {cta.label} {cta.style === 'primary' ? '→' : ''}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export const statsTrendsBlock: BlockDefinition<StatsTrendsData> = {
  meta: {
    type: 'stats-trends',
    label: 'Banda de stats con mini-gráficos (v5)',
    description: 'Banda full-width con eyebrow, título, 2-4 columnas de KPIs con micrográfico SVG y deltas, más CTAs. Variantes lime / ma.',
    category: 'content',
    icon: 'trending-up',
  },
  schema: statsTrendsSchema,
  defaults: {
    variant: 'lime',
    eyebrow: '● Una sola marca · Dos servicios',
    title: 'Un hub que opera',
    titleUnderline: 'venture & M&A',
    titleTail: 'desde el día 1.',
    columns: [
      {
        chart: 'line',
        value: '+€115M',
        label: 'movilizados en operaciones e inversión',
        delta: '↑ +24% vs 2024',
      },
      {
        chart: 'bars',
        value: '+1.500',
        label: 'compradores e inversores activos',
        delta: '↑ +312 últimos 12 meses',
      },
      {
        chart: 'wave',
        value: '17 años',
        label: 'operando empresas y startups',
        delta: 'desde 2009 · ininterrumpidamente',
      },
    ],
    ctas: [
      { label: 'Habla con un asesor', href: '/contacto', style: 'primary' },
      { label: 'Ver casos', href: '/portfolio', style: 'outline' },
    ],
  },
  fields: [
    {
      name: 'variant',
      label: 'Variante de color',
      type: 'select',
      options: [
        { label: 'Lima', value: 'lime' },
        { label: 'M&A (azul)', value: 'ma' },
      ],
    },
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'title', label: 'Título (línea 1)', type: 'text', required: true, max: 200 },
    { name: 'titleUnderline', label: 'Título subrayado (línea 2)', type: 'text', max: 120 },
    { name: 'titleTail', label: 'Título cola (línea 3)', type: 'text', max: 200 },
    {
      name: 'columns',
      label: 'Columnas KPI',
      type: 'array',
      max: 4,
      itemFields: [
        {
          name: 'chart',
          label: 'Tipo de mini-gráfico',
          type: 'select',
          options: [
            { label: 'Línea ascendente', value: 'line' },
            { label: 'Barras', value: 'bars' },
            { label: 'Onda', value: 'wave' },
          ],
        },
        { name: 'value', label: 'Valor (KPI)', type: 'text', required: true, max: 40 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 200 },
        { name: 'delta', label: 'Delta (variación)', type: 'text', max: 120 },
      ],
    },
    {
      name: 'ctas',
      label: 'CTAs (máx 3)',
      type: 'array',
      max: 3,
      itemFields: [
        { name: 'label', label: 'Texto', type: 'text', required: true, max: 80 },
        { name: 'href', label: 'URL', type: 'url', required: true },
        {
          name: 'style',
          label: 'Estilo',
          type: 'select',
          options: [
            { label: 'Primario (sólido)', value: 'primary' },
            { label: 'Outline', value: 'outline' },
          ],
        },
      ],
    },
  ],
  Renderer: StatsTrendsRenderer,
};
